// User Accounts & Subscription Management Types

export interface UserSubscription {
  id: number;
  stripe_subscription_id: string;
  plan_name: 'Monthly Standard' | 'Yearly Standard' | 'Lifetime Plan';
  // 'past_due' and 'pending_cancellation' are also emitted by the backend —
  // the latter by the cancel-at-period-end flow.
  status: 'active' | 'past_due' | 'canceled' | 'pending_cancellation' | 'succeeded' | 'trialing';
  start_date: string;
  end_date: string | null;
  active: boolean;
  canceled_at: string | null;
}

export interface StripeCustomer {
  id: number;
  stripe_customer_id: string;
  stripe_subscription_id: string | null;
  created_at: string;
}

export interface PaymentMethod {
  id: number;
  stripe_payment_method_id: string;
  card_last4: string;
  card_brand: string;
  card_exp_month: number;
  card_exp_year: number;
  created_at: string;
}

export interface PaymentTransaction {
  id: number;
  stripe_payment_intent_id: string;
  amount: string;
  status: 'succeeded' | 'failed';
  created_at: string;
  user_email?: string;
}

export interface UserDevice {
  id: string;
  device_id: string;
  name: string | null;
  linked_at: string | null;
  last_synced_at: string | null;
  firmware_version: string | null;
  pico_version: string | null;
}

export interface DiscountCodeUsed {
  code: string;
  discount_percent: number;
  used_at: string | null;
}

export interface ShopifyPurchase {
  id: number;
  shopify_order_id: string;
  customer_email: string;
  customer_name: string | null;
  product_title: string;
  is_subscription_product: boolean;
  discount_code_sent: boolean;
  created_at: string;
  shopify_order_url: string;
  shopify_customer_url: string;
}

// List item (from GET /api/admin/users/)
export interface UserAccountListItem {
  id: number;
  email: string;
  is_active: boolean;
  is_staff: boolean;
  is_trial_active: boolean;
  trial_start_date: string | null;
  free_plan_expiry_date: string | null;
  has_used_trial: boolean;
  stripe_customer_id: string | null;
  subscription: UserSubscription | null;
  devices_count: number;
}

// Full detail (from GET /api/admin/users/<id>/)
export interface UserAccountDetail extends UserAccountListItem {
  stripe_customer: StripeCustomer | null;
  payment_methods: PaymentMethod[];
  payment_transactions: PaymentTransaction[];
  devices: UserDevice[];
  discount_codes_used: DiscountCodeUsed[];
  shopify_purchases: ShopifyPurchase[];
}

export interface UserAccountStats {
  total_users: number;
  active_trials: number;
  expired_trials_no_subscription: number;
  active_subscriptions: {
    monthly: number;
    yearly: number;
    lifetime: number;
  };
  canceled_subscriptions: number;
  total_revenue_lifetime: string;
  users_with_devices: number;
  recent_signups_7d: number;
  recent_cancellations_7d: number;
}

// Live counts pulled directly from Stripe (bypasses local DB).
// Returned by GET /api/admin/users/stripe-live-stats/ on the backend.
export interface StripeLiveStats {
  monthly_active: number;
  yearly_active: number;
  monthly_price_id: string;
  yearly_price_id: string;
  fetched_at: string;
  source: 'stripe';
  stale: boolean;
  cache_age_seconds: number | null;
  ttl_seconds: number;
  note: string;
  error?: string;
}

export interface UserAccountFilters {
  search?: string;
  plan?: 'monthly' | 'yearly' | 'lifetime' | 'trial' | 'none';
  status?: 'active' | 'canceled' | 'expired_trial';
  ordering?: string;
  page?: number;
  page_size?: number;
}

export interface PaginatedUserAccounts {
  count: number;
  next: string | null;
  previous: string | null;
  results: UserAccountListItem[];
}

export interface UserPatchPayload {
  is_active?: boolean;
  is_trial_active?: boolean;
  free_plan_expiry_date?: string;
  stripe_customer_id?: string;
}

export interface SubscriptionPayload {
  stripe_subscription_id: string;
  plan_name: 'Monthly Standard' | 'Yearly Standard' | 'Lifetime Plan';
  status: string;
  start_date: string;
  end_date?: string;
  active: boolean;
}

export interface SubscriptionListItem {
  id: number;
  user: number;
  user_email?: string;
  stripe_subscription_id: string;
  plan_name: string;
  status: string;
  start_date: string;
  end_date: string | null;
  active: boolean;
  canceled_at: string | null;
}

export interface DiscountCode {
  id: number;
  code: string;
  stripe_coupon_id: string;
  stripe_promotion_code_id: string | null;
  discount_percent: number;
  discount_amount: string;
  is_used: boolean;
  used_at: string | null;
  expires_at: string;
  created_at: string;
  shopify_purchase?: {
    shopify_order_id: string;
    customer_email: string;
    customer_name: string | null;
    product_title: string;
    is_subscription_product: boolean;
  };
}

export const PLAN_NAMES = ['Monthly Standard', 'Yearly Standard', 'Lifetime Plan'] as const;

export const PLAN_FILTERS = [
  { value: '', label: 'All Plans' },
  { value: 'monthly', label: 'Monthly Standard' },
  { value: 'yearly', label: 'Yearly Standard' },
  { value: 'lifetime', label: 'Lifetime Plan' },
  { value: 'trial', label: 'Active Trial' },
  { value: 'none', label: 'No Subscription' },
] as const;

export const STATUS_FILTERS = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'expired_trial', label: 'Expired Trial' },
] as const;
